import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import ReactDOM from 'react-dom';

interface MultiSelectDropdownProps<T> {
    options: T[];
    onSelect: (values: string[]) => void;
    getValue: (option: T) => string;
    getDisplayText: (option: T) => string;
    getGroup?: (option: T) => string;
    placeholder?: string;
    searchPlaceholder?: string;
    showSearch?: boolean;
    dropdownContainerClassName?: string;
    isValidOption?: (option: T) => boolean;
    selectedValues?: string[];
    testId?: string;
    showSelectAll?: boolean;
}

interface GroupedOptions<T> {
    [key: string]: T[];
}

const MultiSelectDropdown = <T,>({
    options,
    onSelect,
    getValue,
    getDisplayText,
    getGroup,
    placeholder = 'Select options',
    searchPlaceholder = 'Search...',
    showSearch = true,
    dropdownContainerClassName = '',
    isValidOption = () => true,
    selectedValues = [],
    testId = 'multi-select',
    showSelectAll = true,
}: MultiSelectDropdownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);

    const filteredOptions = options.filter(option => {
        if (!isValidOption(option)) return false;
        const displayText = getDisplayText(option).toLowerCase();
        return displayText.includes(searchTerm.toLowerCase());
    });

    const groupedOptions: GroupedOptions<T> = getGroup
        ? filteredOptions.reduce((acc, option) => {
            const group = getGroup(option);
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(option);
            return acc;
        }, {} as GroupedOptions<T>)
        : { '': filteredOptions };

    const isAllSelected = selectedValues.length === options.length;

    useEffect(() => {
        if (!isOpen || !triggerRef.current) return;

        const updateDropdownPosition = () => {
            const rect = triggerRef.current!.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        };

        // Initial update
        updateDropdownPosition();

        // Listen for resize of the trigger element
        const resizeObserver = new window.ResizeObserver(updateDropdownPosition);
        resizeObserver.observe(triggerRef.current);

        // Helper to find all scrollable ancestors
        const getScrollableAncestors = (el: HTMLElement | null): (HTMLElement | Window)[] => {
            const ancestors: (HTMLElement | Window)[] = [];
            let current = el?.parentElement;
            while (current) {
                const overflowY = window.getComputedStyle(current).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    ancestors.push(current);
                }
                current = current.parentElement;
            }
            ancestors.push(window); // Always include window
            return ancestors;
        };

        // Attach scroll listeners to all scrollable ancestors
        const ancestors = getScrollableAncestors(triggerRef.current);
        const handleScroll = () => setIsOpen(false);
        ancestors.forEach(ancestor => ancestor.addEventListener('scroll', handleScroll));

        // Also listen for window resize
        window.addEventListener('resize', handleScroll);

        return () => {
            resizeObserver.disconnect();
            ancestors.forEach(ancestor => ancestor.removeEventListener('scroll', handleScroll));
            window.removeEventListener('resize', handleScroll);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node) &&
                (!dropdownRef.current || !dropdownRef.current.contains(event.target as Node))
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
                break;
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    toggleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
        }
    };

    const toggleSelect = (option: T) => {
        const value = getValue(option);
        const newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];

        onSelect(newSelectedValues);
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            onSelect([]);
        } else {
            onSelect(options.map(getValue));
        }
    };

    const removeSelected = (value: string) => {
        onSelect(selectedValues.filter(v => v !== value));
    };

    const renderDropdown = () => {
        if (!isOpen) return null;

        return ReactDOM.createPortal(
            <div
                id={`${testId}-dropdown`}
                data-testid={`${testId}-dropdown`}
                ref={dropdownRef}
                className={`fixed bg-white border border-border rounded-md shadow-lg z-[1000] ${dropdownContainerClassName}`}
                style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}
            >
                {showSearch && (
                    <div
                        id={`${testId}-search-container`}
                        data-testid={`${testId}-search-container`}
                        className="p-2 border-b border-gray-200"
                    >
                        <input
                            id={`${testId}-search-input`}
                            data-testid={`${testId}-search-input`}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full px-3 py-2 border text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}

                <ul
                    ref={optionsRef}
                    className="max-h-60 overflow-y-auto"
                    role="listbox"
                    id={`${testId}-options-list`}
                    data-testid={`${testId}-options-list`}
                >
                    {showSelectAll && filteredOptions.length > 0 && (
                        <li
                            id={`${testId}-option-select-all`}
                            data-testid={`${testId}-option-select-all`}
                            onClick={toggleSelectAll}
                            className={`px-3 py-2 cursor-pointer truncate whitespace-nowrap text-start flex items-center justify-between text-[16px] ${
                                isAllSelected ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
                            }`}
                            role="option"
                            aria-selected={isAllSelected}
                        >
                            Select All
                            {isAllSelected && (
                                <Check
                                    id={`${testId}-option-selected-all`}
                                    data-testid={`${testId}-option-selected-all`}
                                    className="w-4 h-4 ml-2 flex-shrink-0"
                                />
                            )}
                        </li>
                    )}

                    {filteredOptions.length === 0 ? (
                        <li
                            id={`${testId}-no-options`}
                            data-testid={`${testId}-no-options`}
                            className="px-3 py-2 text-gray-500 italic truncate text-start"
                        >
                            No matches found
                        </li>
                    ) : (
                        Object.entries(groupedOptions).map(([group, groupOptions]) => (
                            <React.Fragment key={group}>
                                {group && (
                                    <li className="px-3 py-2 text-[16px] font-semibold">
                                        {group}
                                    </li>
                                )}
                                {groupOptions.map((option, index) => {
                                    const value = getValue(option);
                                    const isSelected = selectedValues.includes(value);
                                    return (
                                        <li
                                            key={value}
                                            id={`${testId}-option-${index}`}
                                            data-testid={`${testId}-option-${index}`}
                                            onClick={() => toggleSelect(option)}
                                            className={`px-6 py-2 cursor-pointer truncate whitespace-nowrap text-start flex items-center justify-between text-[16px] ${
                                                isSelected ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
                                            }`}
                                            role="option"
                                            aria-selected={isSelected}
                                        >
                                            {getDisplayText(option)}
                                            {isSelected && (
                                                <Check
                                                    id={`${testId}-option-selected-${index}`}
                                                    data-testid={`${testId}-option-selected-${index}`}
                                                    className="w-4 h-4 ml-2 flex-shrink-0"
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        ))
                    )}
                </ul>
            </div>,
            document.body
        );
    };

    return (
        <div
            className="relative w-full"
            onKeyDown={handleKeyDown}
            tabIndex={0}
            id={`${testId}-container`}
            data-testid={`${testId}-container`}
        >
            <div
                id={`${testId}-trigger`}
                data-testid={`${testId}-trigger`}
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-3 py-2 border border-border rounded-md flex items-center cursor-pointer"
            >
                {(() => {
                    // Filter out empty/invalid values and remove duplicates
                    const validSelectedValues = Array.from(new Set(selectedValues.filter(val => val && val.trim() !== '')));
                    return validSelectedValues.length > 0 && options.some(option => validSelectedValues.includes(getValue(option))) ? (
                    <div className="flex items-center overflow-hidden whitespace-nowrap w-full">
                        {(() => {
                            const firstSelectedOption = options.find(option => validSelectedValues.includes(getValue(option)));
                            return firstSelectedOption && (
                                <span
                                    key={getValue(firstSelectedOption)}
                                    id={`${testId}-selected-item-${getValue(firstSelectedOption)}`}
                                    data-testid={`${testId}-selected-item-${getValue(firstSelectedOption)}`}
                                    className="flex items-center bg-gray-200 px-2 py-1 rounded-full mr-2 text-xs min-w-0 max-w-full overflow-hidden"
                                >
                                    <span className="truncate">{getDisplayText(firstSelectedOption)}</span>
                                    <X
                                        id={`${testId}-remove-item-${getValue(firstSelectedOption)}`}
                                        data-testid={`${testId}-remove-item-${getValue(firstSelectedOption)}`}
                                        className="w-3 h-3 ml-1 flex-shrink-0 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSelected(getValue(firstSelectedOption));
                                        }}
                                    />
                                </span>
                            );
                        })()}
                        {validSelectedValues.length > 1 && (
                            <span className="text-xs bg-gray-200 px-2 py-1 rounded-full text-gray-700 font-medium">+{validSelectedValues.length - 1} more</span>
                        )}
                    </div>
                ) : (
                    <span
                        id={`${testId}-placeholder`}
                        data-testid={`${testId}-placeholder`}
                        className="text-gray-500"
                    >
                        {placeholder}
                    </span>
                );
                })()}
                <ChevronDown
                    id={`${testId}-chevron`}
                    data-testid={`${testId}-chevron`}
                    className={`w-4 h-4 ml-auto flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {renderDropdown()}
        </div>
    );
};

export default MultiSelectDropdown;